// v3.18-1385 GMAIL-005 — Gmail thread panel scoped to a single client.
// Rendered inside the Client Profile "Communications" tab so operators
// can see, reply, forward, and archive Gmail conversations without
// leaving the client record. Backed by /api/clients/{id}/communications
// which is SECURITY-001 gated.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Mail, Paperclip, Reply, ReplyAll, Forward, Trash2,
  Star, StarOff, Archive, RefreshCw, ChevronDown, Inbox as InboxIcon,
  Send as SendIcon, FileEdit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import GmailComposeModal from './GmailComposeModal';

const FOLDERS = [
  { key: '',        label: 'All',    icon: Mail },
  { key: 'inbox',   label: 'Inbox',  icon: InboxIcon },
  { key: 'sent',    label: 'Sent',   icon: SendIcon },
  { key: 'drafts',  label: 'Drafts', icon: FileEdit },
  { key: 'trash',   label: 'Trash',  icon: Trash2 },
];

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
};

export default function ClientGmailThreadPanel({ clientId }) {
  const [folder, setFolder]   = useState('');
  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [detail, setDetail] = useState(null);
  const [composeCtx, setComposeCtx] = useState({ open: false, mode: 'new', source: null });
  const [refreshTick, setRefreshTick] = useState(0);
  // v3.21-1642 · Authoritative client email pulled fresh from the
  // backend so Compose defaults To to the current record — not stale
  // props and never `recipient@example.com`.
  const [clientEmail, setClientEmail] = useState('');

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const qs = folder ? `?folder=${folder}` : '';
      const [{ data: comms }, { data: mb }, { data: cli }] = await Promise.all([
        api.get(`/clients/${clientId}/communications${qs}`),
        api.get('/google/mailboxes'),
        api.get(`/clients/${clientId}`),
      ]);
      setMessages(comms?.messages || []);
      setThreads(comms?.threads || []);
      setMailboxes(mb?.items || []);
      const addr = (cli?.email || '').trim();
      setClientEmail(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr) ? addr : '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load communications');
    } finally { setLoading(false); }
  }, [clientId, folder, refreshTick]);

  useEffect(() => { load(); }, [load]);

  const threadGroups = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const k = m.thread_id || m.gmail_message_id;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(m);
    }
    return Array.from(map.entries()).map(([tid, msgs]) => ({
      thread_id: tid,
      subject: msgs[0]?.subject || '(no subject)',
      latest: msgs[0],
      count: msgs.length,
      unread: msgs.some((m) => m.is_unread),
      starred: msgs.some((m) => m.is_starred),
      messages: msgs.slice().reverse(),
    })).sort((a, b) => (b.latest?.internal_date || '').localeCompare(a.latest?.internal_date || ''));
  }, [messages]);

  const openThread = (t) => {
    setSelectedThread(t.thread_id);
    setDetail(t);
  };

  const runAction = async (verb, gid) => {
    try {
      if (verb === 'delete') await api.delete(`/gmail/messages/${gid}`);
      else                    await api.post(`/gmail/messages/${gid}/${verb}`);
      toast.success(verb);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="client-gmail-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Mail className="w-4 h-4" /> Gmail thread history
        </div>
        <div className="flex items-center gap-1">
          {FOLDERS.map((f) => (
            <button
              key={f.key || 'all'}
              data-testid={`client-gmail-folder-${f.key || 'all'}`}
              onClick={() => setFolder(f.key)}
              className={
                'px-2 py-1 rounded text-[11px] font-semibold border ' +
                (folder === f.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100')
              }
            >{f.label}</button>
          ))}
          <Button
            size="sm" variant="outline"
            onClick={() => setRefreshTick((n) => n + 1)}
            data-testid="client-gmail-refresh"
            className="ml-1 h-7 text-xs"
          ><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
          <Button
            size="sm"
            data-testid="client-gmail-compose"
            onClick={() => setComposeCtx({ open: true, mode: 'new', source: null })}
            className="h-7 text-xs bg-slate-900 hover:bg-black text-white"
          >Compose</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : threadGroups.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="client-gmail-empty">
          No Gmail activity linked to this client yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="border-r border-slate-100 divide-y divide-slate-100 max-h-[520px] overflow-y-auto">
            {threadGroups.map((t) => (
              <button
                key={t.thread_id}
                data-testid={`client-gmail-thread-${t.thread_id}`}
                onClick={() => openThread(t)}
                className={
                  'block w-full text-left px-3 py-2.5 hover:bg-slate-50 ' +
                  (selectedThread === t.thread_id ? 'bg-slate-100' : '')
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={'text-sm truncate ' + (t.unread ? 'font-bold' : 'font-medium')}>
                    {t.subject}
                  </div>
                  <div className="text-[10px] text-slate-500 whitespace-nowrap">
                    {fmtWhen(t.latest?.internal_date)}
                  </div>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">
                  {t.latest?.snippet || t.latest?.from}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                  <span>{t.count} msg</span>
                  {t.starred && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}
                  {(t.latest?.attachments || []).length > 0 && (
                    <span className="inline-flex items-center gap-0.5">
                      <Paperclip className="w-3 h-3" />
                    </span>
                  )}
                  <span className="ml-auto px-1.5 rounded bg-slate-100 uppercase text-[9px]">
                    {t.latest?.folder}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="p-3 max-h-[520px] overflow-y-auto" data-testid="client-gmail-detail">
            {!detail ? (
              <div className="text-center text-slate-400 text-sm mt-16">
                Select a conversation to preview here.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-sm font-bold text-slate-900 flex-1">{detail.subject}</div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => setComposeCtx({ open: true, mode: 'reply', source: detail.messages[detail.messages.length - 1] })}
                      data-testid="client-gmail-reply"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-900 text-white text-[11px]"
                    ><Reply className="w-3 h-3" /> Reply</button>
                    <button
                      onClick={() => setComposeCtx({ open: true, mode: 'replyAll', source: detail.messages[detail.messages.length - 1] })}
                      data-testid="client-gmail-reply-all"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-800 text-[11px] border border-slate-200"
                    ><ReplyAll className="w-3 h-3" /> Reply all</button>
                    <button
                      onClick={() => setComposeCtx({ open: true, mode: 'forward', source: detail.messages[detail.messages.length - 1] })}
                      data-testid="client-gmail-forward"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 text-slate-800 text-[11px] border border-slate-200"
                    ><Forward className="w-3 h-3" /> Forward</button>
                  </div>
                </div>
                <div className="space-y-4">
                  {detail.messages.map((m) => (
                    <div key={m.gmail_message_id} className="border-t border-slate-100 pt-3">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <div>
                          <b className="text-slate-800">{m.from}</b>
                          <span className="ml-2">to {m.to}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>{fmtWhen(m.internal_date)}</span>
                          <button
                            title="star"
                            onClick={() => runAction(m.is_starred ? 'unstar' : 'star', m.gmail_message_id)}
                          >{m.is_starred ? <Star className="w-3 h-3 fill-amber-400 text-amber-500" /> : <StarOff className="w-3 h-3" />}</button>
                          <button
                            title="archive"
                            onClick={() => runAction('archive', m.gmail_message_id)}
                          ><Archive className="w-3 h-3" /></button>
                          <button
                            title="trash"
                            onClick={() => runAction('trash', m.gmail_message_id)}
                          ><Trash2 className="w-3 h-3 text-red-600" /></button>
                        </div>
                      </div>
                      {(m.attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                          {m.attachments.map((a) => (
                            <span key={a.attachment_id}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">
                              <Paperclip className="w-2.5 h-2.5" /> {a.filename}
                            </span>
                          ))}
                        </div>
                      )}
                      {m.body_html
                        ? <div className="prose prose-sm max-w-none text-slate-800 text-sm"
                              dangerouslySetInnerHTML={{ __html: m.body_html }} />
                        : <div className="whitespace-pre-wrap text-sm text-slate-800">{m.body_text}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <GmailComposeModal
        open={composeCtx.open}
        onClose={() => setComposeCtx({ ...composeCtx, open: false })}
        mailboxes={mailboxes}
        defaultMailboxId={mailboxes[0]?.id}
        mode={composeCtx.mode}
        sourceMessage={composeCtx.source}
        clientId={clientId}
        initialTo={composeCtx.mode === 'new' && clientEmail ? [clientEmail] : []}
        onSent={async () => { setRefreshTick((n) => n + 1); }}
      />
    </div>
  );
}
